import { useWallet } from "@solana/wallet-adapter-react";
import { useWalletModal } from "@solana/wallet-adapter-react-ui";
import Button from "../button/button";
import { FC, useEffect, useRef, useState } from "react";
import { useAuth } from "app/hooks";
import { useWalletSeparation } from "app/hooks/useWalletSeparation";
import { useSolanaWalletAssociation } from "app/hooks/useSolanaWalletAssociation";
import { useWalletConnectionLogger, useErrorLogger, useUserActionLogger } from "app/hooks/useLogger";
import { useSolanaLogger } from "app/hooks/useSolanaLogger";
import axios from "axios";
import tokenService from "services/token.service";
import { useAccount } from "wagmi";
import styles from "./SolanaConnectButton.module.scss";

// Extend Window interface to include phantom
declare global {
  interface Window {
    phantom?: {
      solana?: {
        isPhantom?: boolean;
        connect?: () => Promise<{ publicKey: any }>;
        disconnect?: () => Promise<void>;
        on?: (event: string, callback: Function) => void;
        request?: (method: string, params?: any) => Promise<any>;
      };
    };
  }
}

interface SolanaConnectButtonProps {
  onAssociated?: () => void;
  onError?: (error: string) => void;
  disabled?: boolean;
  className?: string;
}

export const SolanaConnectButton: FC<SolanaConnectButtonProps> = ({
  onAssociated,
  onError,
  disabled = false,
  className,
}) => {
  const {
    publicKey,
    connected,
    disconnect,
    wallets,
    select,
    connect,
    connecting,
    wallet,
  } = useWallet();
  const { setVisible, visible } = useWalletModal();
  const isAuth = useAuth();
  const { address: evmAddress } = useAccount();
  const { handlers } = useWalletSeparation();

  // Logging hooks
  const {
    logConnectionAttempt,
    logConnectionSuccess,
    logConnectionError,
    logWalletDetected
  } = useWalletConnectionLogger();
  const { logJavaScriptError } = useErrorLogger();
  const { logButtonClick } = useUserActionLogger();

  // Enhanced Solana logging with server integration
  const {
    logConnectionAttempt: logSolanaConnectionAttempt,
    logConnectionSuccess: logSolanaConnectionSuccess,
    logConnectionError: logSolanaConnectionError,
    logAssociationAttempt,
    logAssociationSuccess,
    logAssociationError,
    flushLogs
  } = useSolanaLogger();
  const {
    isAssociated: hookIsAssociated,
    isLoading: isAssociationLoading,
    isCreating,
    error: associationError,
    checkAssociation,
    checkAssociationAndAutoAssociate,
  } = useSolanaWalletAssociation({ requiresSolana: true });

  // Local state for additional functionality
  const [isAssociating, setIsAssociating] = useState(false);
  const [isAssociated, setIsAssociated] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasAttemptedAssociation, setHasAttemptedAssociation] = useState(false);
  const associationAttemptRef = useRef(false);
  const lastConnectionRef = useRef<string>("");
  const [isConnecting, setIsConnecting] = useState(false);

  // Sync with hook state
  useEffect(() => {
    setIsAssociated(hookIsAssociated);
  }, [hookIsAssociated]);

  useEffect(() => {
    console.log("Solana wallet state changed:", {
      connected,
      connecting,
      publicKey: publicKey?.toString(),
      walletsCount: wallets.length,
      currentWallet: wallet?.adapter.name,
      isConnecting,
      isAssociated: hookIsAssociated,
      isAssociationLoading,
    });

    if (connected && publicKey && evmAddress && isAuth) {
      setIsConnecting(false);
    }

    if (connecting) {
      setIsConnecting(true);
    }
  }, [
    connected,
    connecting,
    publicKey,
    evmAddress,
    isAuth,
    wallets,
    wallet,
    hookIsAssociated,
    isAssociationLoading,
  ]);

  // Reset association attempt when wallet changes
  useEffect(() => {
    if (connected && publicKey && evmAddress) {
      setHasAttemptedAssociation(false);
      associationAttemptRef.current = false;
    }
  }, [connected, publicKey, evmAddress]);

  // Debug wallet availability and connection state
  useEffect(() => {
    const walletsInfo = wallets.map((w) => ({
      name: w.adapter.name,
      readyState: w.adapter.readyState,
      connected: w.adapter.connected,
      icon: w.adapter.icon,
    }));
    
    // Log wallet detection for available wallets
    wallets.forEach((wallet) => {
      if (wallet.adapter.readyState === 'Installed') {
        logWalletDetected('phantom', {
          walletName: wallet.adapter.name,
          readyState: wallet.adapter.readyState,
          evmAddress
        });
      }
    });

    // Check if window.phantom is available
    if (typeof window !== "undefined") {
      const phantomWindow = window as Window;
      console.log("Window phantom object:", {
        hasPhantom: !!phantomWindow.phantom,
        hasSolana: !!phantomWindow.phantom?.solana,
        isPhantom: !!phantomWindow.phantom?.solana?.isPhantom,
      });
    }
  }, [wallets]);

  // Debug modal visibility changes
  useEffect(() => {
    console.log("Solana wallet modal visibility changed:", visible);

    // Only reset connecting state when modal closes without connection after a longer delay
    // This prevents premature resets during the connection process
    if (!visible && !connected && isConnecting) {
      console.log(
        "Modal closed without connection, will reset state after delay"
      );
      const timeoutId = setTimeout(() => {
        // Double-check connection state before resetting
        if (!connected && isConnecting) {
          console.log("Resetting connection state after modal close");
          setIsConnecting(false);
        }
      }, 3000); // Increased timeout to 3 seconds to allow connection to complete

      return () => clearTimeout(timeoutId);
    }
  }, [visible, connected, isConnecting]);

  // Debug connection state changes
  useEffect(() => {
    console.log("Solana connection state changed:", {
      connected,
      connecting,
      publicKey: publicKey?.toString(),
      walletName: wallet?.adapter.name,
      timestamp: new Date().toISOString(),
    });
  }, [connected, connecting, publicKey, wallet]);

  // Handle successful connection
  useEffect(() => {
    if (connected && publicKey && isConnecting) {
      console.log("Solana wallet connected successfully!");

      // Log successful connection (both local and server)
      logConnectionSuccess('phantom', {
        address: publicKey.toString(),
        walletName: wallet?.adapter.name,
        evmAddress,
        isAuth,
        publicKey: publicKey.toString()
      });

      // Enhanced server logging (async but non-blocking)
      logSolanaConnectionSuccess().catch(error => {
        console.warn('Failed to log Solana connection success:', error);
      });

      setIsConnecting(false);
      setVisible(false); // Close modal on successful connection

      // Trigger association check after a short delay to ensure everything is ready
      if (evmAddress && isAuth) {
        setTimeout(() => {
          void checkAssociationAndAutoAssociate();
        }, 500);
      }
    }
  }, [
    connected,
    publicKey,
    isConnecting,
    setVisible,
    evmAddress,
    isAuth,
    checkAssociationAndAutoAssociate,
  ]);

  // Monitor wallet selection and connection attempts
  useEffect(() => {
    if (connecting && !connected) {
      console.log("Wallet connection in progress...");
    }
  }, [connecting, connected]);

  // Monitor wallet adapter changes and trigger connection
  useEffect(() => {
    if (wallet) {
      console.log("Wallet adapter changed:", {
        name: wallet.adapter.name,
        readyState: wallet.adapter.readyState,
        connected: wallet.adapter.connected,
      });

      // If a wallet is selected but not connected, and we're in connecting state, try to connect
      if (wallet && !connected && !connecting && isConnecting && visible) {
        console.log("Wallet selected, attempting to connect...");
        const attemptConnection = async () => {
          try {
            await connect();
            console.log("Connection attempt completed");
          } catch (error) {
            console.error("Connection failed:", error);
          }
        };

        // Add a small delay to ensure wallet selection is complete
        setTimeout(attemptConnection, 100);
      }
    }
  }, [wallet, connected, connecting, isConnecting, visible, connect]);

  // Handle association errors from the centralized hook
  useEffect(() => {
    if (associationError) {
      setError(associationError);
      if (onError) {
        onError(associationError);
      }
    } else {
      setError(null);
      if (onError) {
        onError("");
      }
    }
  }, [associationError, onError]);

  // Notify parent when association completes
  useEffect(() => {
    if (hookIsAssociated && onAssociated) {
      onAssociated();
    }
  }, [hookIsAssociated, onAssociated]);

  // Flush logs on component unmount
  useEffect(() => {
    return () => {
      flushLogs().catch(error => {
        console.warn('Failed to flush Solana logs on unmount:', error);
      });
    };
  }, [flushLogs]);

  // Legacy methods for backward compatibility with the hook functionality
  const checkAssociationLocal = async () => {
    if (!publicKey || !evmAddress) return false;

    try {
      const token = tokenService.tokens?.accessToken;
      const response = await axios.get(
        `${process.env.REACT_APP_API_SECONDARY}/wallet/association/${evmAddress}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const isAssociated = response.data.isAssociated || false;
      setIsAssociated(isAssociated);
      return isAssociated;
    } catch (error: any) {
      console.error("Error checking wallet association:", error);

      // Log association check error
      logJavaScriptError(
        error instanceof Error ? error : new Error('Association check failed'),
        'SolanaConnectButton_checkAssociation'
      );

      // If API endpoint doesn't exist (404), show error
      if (error.response?.status === 404) {
        console.log("SolanaConnectButton: Association API not available");
        setIsAssociated(false);
        setError(
          "Wallet association service is not available. Please try again later."
        );
        if (onError) {
          onError(
            "Wallet association service is not available. Please try again later."
          );
        }
      } else {
        // For other errors, assume not associated
        setIsAssociated(false);
      }
      return false;
    }
  };

  const checkAssociationAndAutoAssociateLocal = async () => {
    if (!publicKey || !evmAddress || !isAuth) return;

    console.log(
      "Checking association status and auto-associating if needed..."
    );

    const isCurrentlyAssociated = await checkAssociationLocal();

    if (
      !isCurrentlyAssociated &&
      !isAssociating &&
      !hasAttemptedAssociation &&
      !associationAttemptRef.current
    ) {
      console.log("Wallets not associated, starting automatic association...");
      setHasAttemptedAssociation(true);
      associationAttemptRef.current = true;
      await associateWallets();
    } else if (isCurrentlyAssociated) {
      console.log("Wallets already associated!");
    }
  };

  const associateWallets = async () => {
    if (!publicKey || !evmAddress || !isAuth) return;

    setIsAssociating(true);
    setError(null);

    try {
      const token = tokenService.tokens?.accessToken;
      const response = await axios.post(
        `${process.env.REACT_APP_API_SECONDARY}/wallet/association`,
        {
          evmAddress: evmAddress,
          solanaAddress: publicKey.toString(),
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Check if response contains the expected association data
      if (
        response.data &&
        response.data.evmAddress &&
        response.data.solanaAddress
      ) {
        console.log("Wallet association created successfully");

        // Log successful association (both local and server)
        logConnectionSuccess('phantom', {
          address: publicKey.toString(),
          action: 'wallet_association',
          evmAddress: evmAddress,
          solanaAddress: publicKey.toString(),
          associationCreated: true
        });

        // Enhanced server logging (async but non-blocking)
        logAssociationSuccess(response.data?.id).catch(error => {
          console.warn('Failed to log association success:', error);
        });

        setIsAssociated(true);
        setError(null); // Clear any previous errors
        if (onAssociated) {
          onAssociated();
        }
        if (onError) {
          onError(""); // Clear error in parent component
        }
      } else {
        const errorMsg = "Failed to associate wallets";
        setError(errorMsg);

        // Log association failure (both local and server)
        const associationError = new Error(errorMsg);
        logConnectionError('phantom', associationError, {
          evmAddress: evmAddress,
          solanaAddress: publicKey.toString(),
          responseData: response.data
        });

        // Enhanced server logging (async but non-blocking)
        logAssociationError(associationError, 'association_creation', {
          responseData: response.data
        }).catch(error => {
          console.warn('Failed to log association error:', error);
        });
      }
    } catch (error: any) {
      console.error("Error associating wallets:", error);

      // Log association error (both local and server)
      const associationError = error instanceof Error ? error : new Error('Wallet association failed');
      logConnectionError('phantom', associationError, {
        evmAddress: evmAddress,
        solanaAddress: publicKey.toString(),
        statusCode: error.response?.status,
        errorData: error.response?.data
      });

      // Enhanced server logging (async but non-blocking)
      logAssociationError(associationError, 'association_creation', {
        statusCode: error.response?.status,
        errorData: error.response?.data
      }).catch(serverError => {
        console.warn('Failed to log association error:', serverError);
      });

      // If API endpoint doesn't exist (404), just mark as associated for now
      if (error.response?.status === 404) {
        console.log(
          "SolanaConnectButton: Association API not available, marking as associated locally"
        );
        setIsAssociated(true);
        setError(null);
        if (onAssociated) {
          onAssociated();
        }
        if (onError) {
          onError(""); // Clear error in parent component
        }
      } else {
        // Real error that should prevent token purchases
        const errorMessage =
          error.response?.data?.message ||
          "Failed to associate Solana wallet. Please try again.";
        setError(errorMessage);
        if (onError) {
          onError(errorMessage);
        }
      }
    } finally {
      setIsAssociating(false);
      associationAttemptRef.current = false;
    }
  };

  const handleConnect = async () => {
    if (!connected && !isConnecting) {
      console.log("Starting Solana wallet connection...");

      // Log connection attempt (both local and server)
      logConnectionAttempt('phantom', {
        evmAddress,
        isAuth,
        availableWallets: wallets.length,
        walletNames: wallets.map(w => w.adapter.name)
      });

      // Enhanced server logging (async but non-blocking)
      logSolanaConnectionAttempt({
        availableWallets: wallets.length,
        walletNames: wallets.map(w => w.adapter.name)
      }).catch(error => {
        console.warn('Failed to log Solana connection attempt:', error);
      });

      setIsConnecting(true);

      // Open modal for wallet selection - this is more reliable than direct connection
      console.log("Opening wallet selection modal...");
      setVisible(true);

      // Add timeout to reset connecting state if no connection happens
      const timeoutId = setTimeout(() => {
        if (!connected && isConnecting) {
          console.log("Connection timeout after 30 seconds, resetting state");

          // Log connection timeout error (both local and server)
          const timeoutError = new Error('Connection timeout after 30 seconds');
          logConnectionError('phantom', timeoutError, {
            evmAddress,
            availableWallets: wallets.length,
            timeoutDuration: 30000
          });

          // Enhanced server logging (async but non-blocking)
          logSolanaConnectionError(timeoutError, {
            availableWallets: wallets.length,
            timeoutDuration: 30000
          }).catch(error => {
            console.warn('Failed to log Solana connection timeout:', error);
          });

          setIsConnecting(false);
          setVisible(false); // Close modal on timeout
        }
      }, 30000);

      // Store timeout ID to clear it if connection succeeds
      return () => clearTimeout(timeoutId);
    } else if (isConnecting) {
      console.log("Connection already in progress...");
    } else {
      console.log("Wallet already connected:", {
        publicKey: publicKey?.toString(),
      });
    }
  };

  const handleDisconnect = () => {
    // Use the separation handler to ensure EVM connection is maintained
    handlers.handleSolanaDisconnect();
    setIsAssociated(false);
    setError(null);
    setHasAttemptedAssociation(false);
    associationAttemptRef.current = false;
    lastConnectionRef.current = "";
  };

  if (!isAuth || !evmAddress) {
    return (
      <div className={`${styles.solanaConnectButton} ${className || ""}`}>
        <p className={styles.requirementText}>Connect EVM wallet first</p>
      </div>
    );
  }

  // Use hook state as primary source of truth, fall back to local state
  const finalIsAssociated = hookIsAssociated || isAssociated;
  // Don't block UI for association creation - only for initial loading
  const finalIsLoading = isAssociationLoading || isAssociating;

  return (
    <div className={`${styles.solanaConnectButton} ${className || ""}`}>
      {!connected ? (
        <Button
          onClick={() => {
            logButtonClick('solana-connect-button', 'SolanaConnectButton', {
              evmAddress,
              isAuth,
              isConnecting,
              availableWallets: wallets.length
            });
            handleConnect();
          }}
          disabled={disabled || isConnecting}
          variant="outlined"
          className={styles.connectButton}
        >
          {isConnecting ? "Connecting..." : "Connect Solana Wallet"}
        </Button>
      ) : finalIsAssociated ? (
        <div className={styles.connectedState}>
          <div className={styles.walletInfo}>
            <span className={styles.successIcon}>✓</span>
            <span className={styles.walletAddress}>
              {publicKey?.toString().slice(0, 4)}...
              {publicKey?.toString().slice(-4)}
            </span>
          </div>
          <Button
            onClick={() => {
              logButtonClick('solana-disconnect-button', 'SolanaConnectButton', {
                evmAddress,
                publicKey: publicKey?.toString(),
                isAssociated: finalIsAssociated
              });
              handleDisconnect();
            }}
            variant="outlined"
            className={styles.disconnectButton}
          >
            Disconnect
          </Button>
        </div>
      ) : (
        <div className={styles.associationState}>
          <div className={styles.walletInfo}>
            <span className={styles.walletAddress}>
              {publicKey?.toString().slice(0, 4)}...
              {publicKey?.toString().slice(-4)}
            </span>
          </div>
          <div className={styles.buttonGroup}>
            {finalIsLoading ? (
              <p className={styles.loadingText}>
                Associating wallets automatically...
              </p>
            ) : (
              <>
                <p className={styles.statusText}>
                  Wallet connected, association in progress...
                </p>
                <Button
                  onClick={handleDisconnect}
                  variant="outlined"
                  className={styles.disconnectButton}
                >
                  Disconnect
                </Button>
              </>
            )}
          </div>
        </div>
      )}
      {error && <p className={styles.errorText}>{error}</p>}
    </div>
  );
};
